import { Box, Button, Icon, Menu, MenuItem, Tooltip } from "@mui/material";
import { useContext, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExportButton } from "~/components/Buttons";
import GridExportContext from "~/providers/GridExportContext";
import GridFileExportContext from "~/providers/GridFileExportContext";

export interface ImportMenuItem {
	label: string;
	onClick: () => void;
}

type ExportImportBoxProps = {
	disableExport?: boolean;
	disableImport?: boolean;
	exportTooltip?: string;
	hideImport?: boolean;
	importDisabledReason?: string;
	importMenuItems?: ImportMenuItem[];
	onExport?: () => void;
	onImport?: () => void;
};

const ExportImportBox = ({
	disableExport = false,
	disableImport = false,
	exportTooltip,
	hideImport = false,
	importDisabledReason,
	importMenuItems,
	onExport,
	onImport
}: ExportImportBoxProps) => {
	const { t } = useTranslation("common");
	const gridExport = useContext(GridExportContext);
	const fileExport = useContext(GridFileExportContext);
	const [importMenuAnchor, setImportMenuAnchor] = useState<HTMLElement | null>(null);
	const importReasonId = useId();
	const exportReasonId = useId();
	const importReason = disableImport ? importDisabledReason : undefined;
	const formatMenuExport = onExport ? undefined : fileExport;
	const effectiveExport = onExport ?? gridExport;
	const exportDisabled = disableExport || (formatMenuExport ? formatMenuExport.isExporting : !effectiveExport);

	const handleExport = (event: React.MouseEvent<HTMLElement>) => {
		if (formatMenuExport) {
			formatMenuExport.openFormatMenu(event.currentTarget);
			return;
		}
		effectiveExport?.();
	};

	const handleImport = (event: React.MouseEvent<HTMLElement>) => {
		if (importMenuItems?.length) {
			setImportMenuAnchor(event.currentTarget);
			return;
		}
		onImport?.();
	};

	const handleMenuItemClick = (item: ImportMenuItem) => {
		setImportMenuAnchor(null);
		item.onClick();
	};

	const exportButton = (
		<ExportButton
			aria-describedby={exportTooltip ? exportReasonId : undefined}
			aria-haspopup={formatMenuExport ? "menu" : undefined}
			disabled={exportDisabled}
			onClick={handleExport}
			size="small"
			startIcon={<Icon sx={{ color: exportDisabled ? "action.disabled" : "text.primary" }}>download</Icon>}
			sx={{
				color: "text.primary",
				fontWeight: 400,
				justifyContent: "flex-start",
				textTransform: "none",
				width: "100%"
			}}
			variant="text"
		/>
	);

	const importButton = (
		<Button
			aria-describedby={importReason ? importReasonId : undefined}
			disabled={disableImport}
			onClick={handleImport}
			size="small"
			startIcon={<Icon sx={{ color: disableImport ? "action.disabled" : "text.primary" }}>upload</Icon>}
			sx={{
				color: "text.primary",
				fontWeight: 400,
				justifyContent: "flex-start",
				textTransform: "none",
				width: "100%"
			}}
			variant="text"
		>
			{t("grid.ribbon.import")}
		</Button>
	);

	return (
		<Box
			sx={{
				alignItems: "center",
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				height: "100%",
				justifyContent: "center"
			}}
		>
			{exportTooltip ? (
				<Tooltip title={exportTooltip}>
					<Box component="span" sx={{ display: "flex", width: "100%" }}>
						{exportButton}
						<Box component="span" hidden id={exportReasonId}>
							{exportTooltip}
						</Box>
					</Box>
				</Tooltip>
			) : (
				exportButton
			)}
			{!hideImport &&
				(importReason ? (
					<Tooltip title={importReason}>
						<Box component="span" sx={{ display: "flex", width: "100%" }}>
							{importButton}
							<span hidden id={importReasonId}>
								{importReason}
							</span>
						</Box>
					</Tooltip>
				) : (
					importButton
				))}
			{importMenuItems && (
				<Menu anchorEl={importMenuAnchor} onClose={() => setImportMenuAnchor(null)} open={Boolean(importMenuAnchor)}>
					{importMenuItems.map((item) => (
						<MenuItem key={item.label} onClick={() => handleMenuItemClick(item)}>
							{item.label}
						</MenuItem>
					))}
				</Menu>
			)}
		</Box>
	);
};

export default ExportImportBox;
